import React from 'react';
import { UseFormReturn } from "react-hook-form";
import { SectionCard } from "./components/SectionCard";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { FormField, FormItem, FormLabel, FormControl } from "@/components/ui/form";
import { useCampExperienceOptions } from "../hooks/useCampExperienceOptions";
import { ExperienceScales } from './components/ExperienceScales';
import { ThemeSelectionSection } from './components/ThemeSelectionSection';
import { OptionsSection } from './components/OptionsSection';
import { ApplyToAllButtonWithConfirm } from "../components/ApplyToAllButtonWithConfirm";

interface CampExperienceSectionsProps {
  form: UseFormReturn<any>;
  onApplyAllergyAccommodationToAll?: (value: { allergyKeys: string[]; allergyNotes: string }) => Promise<void>;
  onApplyReligiousAffiliationToAll?: (value: { religiousAffiliationKeys: string[]; religiousAffiliationNotes: string }) => Promise<void>;
  onApplySupportForToAll?: (value: { supportForKeys: string[]; supportForNotes: string }) => Promise<void>;
  applyToAllLoading?: boolean;
}

export const CampExperienceSections = ({
  form,
  onApplyAllergyAccommodationToAll,
  onApplyReligiousAffiliationToAll,
  onApplySupportForToAll,
  applyToAllLoading = false,
}: CampExperienceSectionsProps) => {
  const {
    themeOptions,
    allergyOptions,
    religiousOptions,
    supportOptions,
    loading
  } = useCampExperienceOptions();

  if (loading) {
    return <div>Loading options...</div>;
  }

  return <>
      <SectionCard title="The camp experience">
        <ThemeSelectionSection form={form} themeOptions={themeOptions} />
        
        <FormField control={form.control} name="campExperiences.0.topActivities" render={({
        field
      }) => <FormItem className="text-left">
              <FormLabel className="">Top activities (optional)</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Enter top activities (comma separated)" maxLength={200} />
              </FormControl>
            </FormItem>} />

        <div className="flex justify-between items-center mt-12">
          <div className="text-sm text-muted-foreground">Drag each slider toward the side that best describes the camp experience.</div>
          
          <FormField
            control={form.control}
            name="campExperiences.0.hideScales"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value || false}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel className="text-sm font-normal cursor-pointer text-left">
                  Hide these scales (recommended if <br/>you offer very different camp experiences)
                </FormLabel>
              </FormItem>
            )}
          />
        </div>
        
        <ExperienceScales form={form} isHidden={form.watch("campExperiences.0.hideScales")} />
      </SectionCard>

      <OptionsSection
        form={form}
        title="Allergy accommodation"
        description="This section is optional. It will be omitted from your camp page if left blank."
        options={allergyOptions}
        optionsFieldName="allergyKeys"
        notesFieldName="allergyNotes"
        notesPlaceholder="Enter additional notes (optional)"
        headerRight={
          onApplyAllergyAccommodationToAll ? (
            <ApplyToAllButtonWithConfirm
              disabled={applyToAllLoading}
              dialogTitle="Apply to all locations?"
              dialogDescription="This will overwrite Allergy accommodation for every location that shares the same camp organization. This updates drafts and cannot be undone."
              onConfirm={async () => {
                const allergyKeys = (form?.getValues?.("allergyKeys") ?? []) as string[];
                const allergyNotes = String(form?.getValues?.("allergyNotes") ?? "");
                await onApplyAllergyAccommodationToAll({ allergyKeys: Array.isArray(allergyKeys) ? allergyKeys : [], allergyNotes });
              }}
            />
          ) : null
        }
      />

      <OptionsSection
        form={form}
        title="Religious affiliation"
        description="This section is optional. It will be omitted from your camp page if left blank."
        options={religiousOptions}
        optionsFieldName="religiousAffiliationKeys"
        notesFieldName="religiousAffiliationNotes"
        notesPlaceholder="Enter additional notes (optional)"
        headerRight={
          onApplyReligiousAffiliationToAll ? (
            <ApplyToAllButtonWithConfirm
              disabled={applyToAllLoading}
              dialogTitle="Apply to all locations?"
              dialogDescription="This will overwrite Religious affiliation for every location that shares the same camp organization. This updates drafts and cannot be undone."
              onConfirm={async () => {
                const religiousAffiliationKeys = (form?.getValues?.("religiousAffiliationKeys") ?? []) as string[];
                const religiousAffiliationNotes = String(form?.getValues?.("religiousAffiliationNotes") ?? "");
                await onApplyReligiousAffiliationToAll({
                  religiousAffiliationKeys: Array.isArray(religiousAffiliationKeys) ? religiousAffiliationKeys : [],
                  religiousAffiliationNotes,
                });
              }}
            />
          ) : null
        }
      />

      <OptionsSection
        form={form}
        title="Support for campers with disabilities"
        description="This section is optional. It will be omitted from your camp page if left blank."
        options={supportOptions}
        optionsFieldName="supportForKeys"
        notesFieldName="supportForNotes"
        notesPlaceholder="Enter additional notes (optional)"
        headerRight={
          onApplySupportForToAll ? (
            <ApplyToAllButtonWithConfirm
              disabled={applyToAllLoading}
              dialogTitle="Apply to all locations?"
              dialogDescription="This will overwrite Support for campers with disabilities for every location that shares the same camp organization. This updates drafts and cannot be undone."
              onConfirm={async () => {
                const supportForKeys = (form?.getValues?.("supportForKeys") ?? []) as string[];
                const supportForNotes = String(form?.getValues?.("supportForNotes") ?? "");
                await onApplySupportForToAll({ supportForKeys: Array.isArray(supportForKeys) ? supportForKeys : [], supportForNotes });
              }}
            />
          ) : null
        }
      />
    </>;
};